import { useState, useEffect, useRef } from 'react';
import { 
  isChromeExtensionAvailable, 
  getOnshapeContextFromActiveTab,
  parseOnshapeUrl 
} from '@/lib/chrome-extension';

/**
 * Hook to extract Onshape context from URL parameters when embedded in Onshape
 * 
 * PRIMARY AND ONLY RELIABLE METHOD: URL Parameters
 * When Onshape loads your extension (Right Panel, Tab, or Dialog), it automatically appends
 * context as query parameters: ?documentId=...&workspaceId=...&elementId=...
 * This is the "Clear Path" according to Onshape API documentation.
 * 
 * IMPORTANT NOTES:
 * - Chrome Tabs API (chrome.tabs) is NOT available for Onshape App Store extensions (iframes)
 * - Onshape Client SDK does NOT have methods to detect context - it requires you to provide it
 * - Parent window URL parsing is blocked by cross-origin restrictions
 * - URL parameters are the ONLY reliable method for Onshape extensions
 * 
 * If URL parameters are missing, the extension may not be properly configured in Onshape.
 * Ensure your extension is registered and loaded as a Right Panel, Tab, or Dialog extension.
 * 
 * @returns {Object} { documentId, workspaceId, elementId, server, userId, access, isValid }
 */
export function useOnshapeContext() {
  const hasValidContextRef = useRef(false);
  
  const [context, setContext] = useState(() => {
    // PRIMARY METHOD: Extract parameters from URL query string
    // Onshape automatically appends these when loading extensions (Right Panel, Tab, or Dialog)
    // This is the "Clear Path" according to Onshape API documentation
    const params = new URLSearchParams(window.location.search);
    
    // Try standard parameter names first (as per Onshape docs)
    const documentId = params.get('documentId') || params.get('did');
    const workspaceId = params.get('workspaceId') || params.get('versionId') || params.get('microversionId') || params.get('wid');
    const elementId = params.get('elementId') || params.get('eid');
    const server = params.get('server') ? decodeURIComponent(params.get('server')) : 'https://cad.onshape.com';
    const userId = params.get('userId');
    const access = params.get('access'); // 'edit' or 'read'
    
    // Validate required parameters (documentId, workspaceId, elementId are required)
    const isValid = !!(documentId && workspaceId && elementId);
    
    return {
      documentId,
      workspaceId,
      elementId,
      server,
      userId,
      access,
      isValid,
    };
  });

  useEffect(() => {
    const tryGetSDKContext = async () => {
      let documentId = null;
      let workspaceId = null;
      let elementId = null;
      let server = null;
      
      // Method 0: URL Parameters (PRIMARY METHOD - as per Onshape API docs)
      // Onshape automatically appends context as query parameters when loading extensions
      // This is the "Clear Path" according to Onshape documentation
      const params = new URLSearchParams(window.location.search);
      const urlDocumentId = params.get('documentId') || params.get('did');
      const urlWorkspaceId = params.get('workspaceId') || params.get('versionId') || params.get('microversionId') || params.get('wid');
      const urlElementId = params.get('elementId') || params.get('eid');
      const urlServer = params.get('server') ? decodeURIComponent(params.get('server')) : null;
      
      // Helper function to detect template strings
      const isTemplateStringValue = (value) => {
        if (!value || typeof value !== 'string') return false;
        // Check for template string patterns
        return (
          value === '{documentId}' ||
          value === '{workspaceId}' ||
          value === '{elementId}' ||
          value === '{versionId}' ||
          value === '{microversionId}' ||
          value.includes('${') ||
          (value.startsWith('{') && value.endsWith('}') && value.length > 2)
        );
      };
      
      const hasTemplateStrings = isTemplateStringValue(urlDocumentId) || 
                                 isTemplateStringValue(urlWorkspaceId) || 
                                 isTemplateStringValue(urlElementId);
      
      if (hasTemplateStrings) {
        // Template strings detected - Onshape is not substituting variables
        // This usually means extension opened from wrong context or Action URL syntax issue
      } else if (urlDocumentId && urlWorkspaceId && urlElementId) {
        documentId = urlDocumentId;
        workspaceId = urlWorkspaceId;
        elementId = urlElementId;
        server = urlServer;
      } else if (urlDocumentId || urlWorkspaceId || urlElementId) {
        // Partial parameters - still try to use what we have
        documentId = urlDocumentId || documentId;
        workspaceId = urlWorkspaceId || workspaceId;
        elementId = urlElementId || elementId;
        server = urlServer || server;
      } else {
        // URL parameters are missing - attempt to request context via postMessage
        
        // Try to request context via postMessage (similar to Onshape Client SDK)
        // This is a long shot, but worth trying
        try {
          if (window.parent && window.parent !== window) {
            // Request document context from Onshape parent
            window.parent.postMessage({
              type: 'GET_DOCUMENT_CONTEXT',
              source: 'fastenai-extension'
            }, '*');
            
            // Set up listener for response
            const handleContextMessage = (event) => {
              if (event.origin !== 'https://cad.onshape.com') return;
              // Only accept messages that look like they're from Onshape
              if (event.data && (
                event.data.documentId || 
                event.data.documentContext ||
                event.data.type === 'DOCUMENT_CONTEXT_RESPONSE'
              )) {
                const contextData = event.data.documentContext || event.data;
                if (contextData.documentId) {
                  const receivedDocumentId = contextData.documentId;
                  const receivedWorkspaceId = contextData.workspaceId || contextData.workspaceId;
                  const receivedElementId = contextData.elementId || contextData.elementId;
                  
                  if (receivedDocumentId && receivedWorkspaceId && receivedElementId) {
                    window.removeEventListener('message', handleContextMessage);
                    setContext({
                      documentId: receivedDocumentId,
                      workspaceId: receivedWorkspaceId,
                      elementId: receivedElementId,
                      server: server || 'https://cad.onshape.com',
                      userId: params.get('userId'),
                      access: params.get('access'),
                      isValid: true,
                    });
                  }
                }
              }
            };
            
            window.addEventListener('message', handleContextMessage);
            
            // Clean up listener after timeout
            setTimeout(() => {
              window.removeEventListener('message', handleContextMessage);
            }, 5000);
          }
        } catch (err) {
          // Error attempting postMessage - expected in many cases
        }
      }
      
      // Method 2: Try Onshape Client SDK (NOTE: This won't work - SDK doesn't detect context)
      // The Onshape Client SDK requires you to provide context, it doesn't detect it
      if ((!documentId || !workspaceId || !elementId) && typeof window !== 'undefined' && window.OnshapeClient) {
        try {
          const client = new window.OnshapeClient();
          
          // Try various SDK methods to get context (these likely don't exist)
          if (typeof client.getDocumentId === 'function') {
            documentId = await client.getDocumentId();
          }
          if (typeof client.getWorkspaceId === 'function') {
            workspaceId = await client.getWorkspaceId();
          }
          if (typeof client.getElementId === 'function') {
            elementId = await client.getElementId();
          }
          if (typeof client.getContext === 'function') {
            const ctx = await client.getContext();
            if (ctx) {
              documentId = ctx.documentId || documentId;
              workspaceId = ctx.workspaceId || workspaceId;
              elementId = ctx.elementId || elementId;
            }
          }
        } catch (err) {
          // SDK error - expected, SDK doesn't detect context
        }
      }
      
      // Method 3: Try to parse from parent window URL (NOTE: This will fail due to cross-origin restrictions)
      // Cross-origin restrictions prevent accessing window.parent.location.href
      // Keeping this for debugging purposes only
      // This is the Onshape document URL like: https://cad.onshape.com/documents/{did}/w/{wid}/e/{eid}
      if (!documentId || !workspaceId || !elementId) {
        try {
          if (window.parent && window.parent !== window) {
            // Try direct access first
            try {
              const parentUrl = window.parent.location.href;
              
              // Parse Onshape URL format: /documents/{did}/w/{wid}/e/{eid}
              const docMatch = parentUrl.match(/\/documents\/([a-f0-9]{24})/);
              const workspaceMatch = parentUrl.match(/\/w\/([a-f0-9]{24})/);
              const elementMatch = parentUrl.match(/\/e\/([a-f0-9]{24})/);
              
              if (docMatch) documentId = docMatch[1];
              if (workspaceMatch) workspaceId = workspaceMatch[1];
              if (elementMatch) elementId = elementMatch[1];
            } catch (directAccessError) {
              // Cross-origin restrictions - this is expected and will always fail
              // Try postMessage (unlikely to work - Onshape doesn't listen for this)
              // Request URL from parent window via postMessage
              const requestUrl = () => {
                window.parent.postMessage({ type: 'REQUEST_DOCUMENT_URL' }, '*');
              };
              
              // Set up listener for response
              const handleMessage = (event) => {
                if (event.origin !== 'https://cad.onshape.com') return;
                if (event.data && event.data.type === 'DOCUMENT_URL_RESPONSE' && event.data.url) {
                  const parentUrl = event.data.url;
                  
                  const docMatch = parentUrl.match(/\/documents\/([a-f0-9]{24})/);
                  const workspaceMatch = parentUrl.match(/\/w\/([a-f0-9]{24})/);
                  const elementMatch = parentUrl.match(/\/e\/([a-f0-9]{24})/);
                  
                  if (docMatch) documentId = docMatch[1];
                  if (workspaceMatch) workspaceId = workspaceMatch[1];
                  if (elementMatch) elementId = elementMatch[1];
                  
                  if (documentId && workspaceId && elementId) {
                    window.removeEventListener('message', handleMessage);
                    // Update context with parsed values
                    const params = new URLSearchParams(window.location.search);
                    const server = params.get('server') ? decodeURIComponent(params.get('server')) : 'https://cad.onshape.com';
                    const userId = params.get('userId');
                    const access = params.get('access');
                    setContext({
                      documentId,
                      workspaceId,
                      elementId,
                      server,
                      userId,
                      access,
                      isValid: true,
                    });
                  }
                }
              };
              
              window.addEventListener('message', handleMessage);
              requestUrl();
              
              // Also try reading from window.location (might work in some cases)
              // Sometimes the parent URL is accessible via document.referrer
              if (document.referrer) {
                const referrerUrl = document.referrer;
                const docMatch = referrerUrl.match(/\/documents\/([a-f0-9]{24})/);
                const workspaceMatch = referrerUrl.match(/\/w\/([a-f0-9]{24})/);
                const elementMatch = referrerUrl.match(/\/e\/([a-f0-9]{24})/);
                
                if (docMatch) documentId = docMatch[1];
                if (workspaceMatch) workspaceId = workspaceMatch[1];
                if (elementMatch) elementId = elementMatch[1];
              }
            }
          }
        } catch (err) {
          // Error accessing parent URL - expected due to cross-origin restrictions
        }
      }
      
      // Use server from URL params if available, otherwise default
      if (!server) {
        const finalParams = new URLSearchParams(window.location.search);
        server = finalParams.get('server') ? decodeURIComponent(finalParams.get('server')) : 'https://cad.onshape.com';
      }
      
      // Get userId and access from URL params (if available)
      const finalParams = new URLSearchParams(window.location.search);
      const userId = finalParams.get('userId');
      const access = finalParams.get('access');
      
      // Validate - check if we have actual values (not template strings)
      const hasTemplateStringsInValues = isTemplateStringValue(documentId) || 
                                         isTemplateStringValue(workspaceId) || 
                                         isTemplateStringValue(elementId);
      
      // Only mark as valid if we have real values, not template strings
      const isValid = !!(documentId && workspaceId && elementId && !hasTemplateStringsInValues);
      
      // Update ref to track valid context state
      hasValidContextRef.current = isValid;
      
      setContext({
        documentId,
        workspaceId,
        elementId,
        server,
        userId,
        access,
        isValid,
      });
    };

    tryGetSDKContext();
    
    // Set up listeners for URL changes
    const handleLocationChange = () => {
      tryGetSDKContext();
    };
    
    // Listen for browser navigation
    window.addEventListener('popstate', handleLocationChange);
    
    // Listen for Chrome tab updates (if Chrome extension APIs available)
    let tabUpdateListener = null;
    if (isChromeExtensionAvailable() && chrome.tabs && chrome.tabs.onUpdated) {
      tabUpdateListener = (tabId, changeInfo, tab) => {
        // Only update if URL changed and it's an Onshape URL
        if (changeInfo.url && tab.url && tab.url.includes('onshape.com')) {
          tryGetSDKContext();
        }
      };
      
      chrome.tabs.onUpdated.addListener(tabUpdateListener);
    }
    
    // Poll for URL changes (fallback for cases where listeners don't fire)
    // Only poll if we don't have valid context yet
    let pollInterval = null;
    if (isChromeExtensionAvailable()) {
      pollInterval = setInterval(() => {
        // Only poll if context is invalid (to avoid unnecessary API calls)
        if (!hasValidContextRef.current) {
          tryGetSDKContext();
        }
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      if (tabUpdateListener && chrome.tabs && chrome.tabs.onUpdated) {
        chrome.tabs.onUpdated.removeListener(tabUpdateListener);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, []);


  return context;
}

